import Seo from "@/components/Seo";
import { PageHero, Btn, DevisCta } from "@/components/Shared";
import { BookOpen, FileText, Globe2, ShieldCheck, CalendarClock } from "lucide-react";

const LIVRES = [
  {
    icon: FileText,
    titre: "L’IA sans jargon",
    sousTitre: "Le guide de référence pour comprendre et utiliser l’IA au travail",
    points: ["110 fiches pratiques", "155 pages", "Glossaire de 177 termes", "Sections RGPD et AI Act"],
    prix: "10 € TTC — édition numérique PDF",
    lien: "https://payhip.com/b/vdIa6",
    amazon: "https://www.amazon.fr/dp/B0HFGLF8SM",
    testid: "livre-ia",
  },
  {
    icon: Globe2,
    titre: "Atlas économique mondial",
    sousTitre: "193 pays en fiches claires, chiffres officiels datés",
    points: ["193 pays couverts", "209 pages couleur", "Chiffres officiels datés", "Format fiches, consultation rapide"],
    prix: "10 € TTC — édition numérique PDF",
    lien: "https://payhip.com/b/HmCey",
    amazon: "https://www.amazon.fr/dp/B0HFGHJBM9",
    testid: "livre-atlas",
  },
  {
    icon: ShieldCheck,
    titre: "Hygiène et sécurité alimentaire",
    sousTitre: "Le référentiel pratique pour les professionnels de la restauration",
    points: ["142 fiches pratiques", "Format fiches, consultation rapide"],
    prix: "10 € TTC — édition numérique PDF",
    lien: "https://payhip.com/b/Gtakj",
    testid: "livre-hygiene",
  },
];

export default function Livres() {
  return (
    <div data-testid="page-livres">
      <Seo
        title="Livres & ressources — Collection « Le Monde en fiches » — Form Action Pro"
        description="Livres de François Schoonheere : « L'IA sans jargon », « Atlas économique mondial » et « Hygiène et sécurité alimentaire ». Éditions numériques PDF à 10 € TTC et versions papier sur Amazon."
        path="/livres"
      />
      <PageHero
        kicker="Livres & ressources"
        title="La collection « Le Monde en fiches »"
        subtitle="Des ouvrages de référence clairs et structurés, nés du même souci : rendre simple ce qui ne l’est pas. Écrits et publiés par François Schoonheere."
      />

      <section className="bg-sable py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {LIVRES.map((l) => (
            <div
              key={l.titre}
              data-testid={l.testid}
              className="bg-white border border-bordure rounded-lg p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] flex flex-col gap-5"
            >
              <l.icon className="text-vert-dark" size={32} />
              <div>
                <h2 className="font-serif text-2xl text-marine mb-1">{l.titre}</h2>
                <p className="font-sans text-sm text-gris-texte">{l.sousTitre}</p>
              </div>
              <ul className="space-y-2 flex-1">
                {l.points.map((p) => (
                  <li key={p} className="font-sans text-sm text-marine/85 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-vert shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <p className="font-serif text-[28px] text-marine leading-none">{l.prix}</p>
              <div className="flex flex-col gap-3">
                <Btn href={l.lien} external testid={`${l.testid}-acheter`} variant="primary" className="w-full">
                  Commander le PDF
                </Btn>
                {l.amazon && (
                  <a
                    href={l.amazon}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${l.testid}-amazon`}
                    className="inline-flex items-center justify-center gap-2 font-sans font-semibold text-sm px-5 py-3 rounded border border-marine text-marine hover:bg-marine/5 transition-colors duration-200"
                  >
                    Version papier sur Amazon
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-8 text-center">
          <a
            href="https://francois-schoonheere.tinypages.co/livres"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="livres-lien-mes-livres"
            className="font-sans font-semibold text-sm text-vert-dark hover:underline"
          >
            Mes livres — voir la boutique complète →
          </a>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-10">
          <div className="bg-white border border-bordure rounded-lg p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <CalendarClock className="text-vert-dark shrink-0" size={30} />
            <div>
              <h3 className="font-serif text-xl text-marine mb-1">À paraître</h3>
              <p className="font-sans text-sm text-gris-texte leading-relaxed">
                La collection s’enrichit : deux autres tomes sont en préparation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <BookOpen className="text-vert-dark mx-auto mb-4" size={28} />
          <h2 className="font-serif text-3xl text-marine mb-4">Le petit dico de l’IA — offert</h2>
          <p className="font-sans text-base text-marine/80 leading-[1.7] mb-8">
            Le glossaire de <em>L’IA sans jargon</em> est offert séparément : 177 termes, 25 pages,
            avec les équivalents français officiels.
          </p>
          <Btn to="/dico-ia" testid="livres-cta-dico" variant="dark">
            Découvrir le dico offert
          </Btn>
        </div>
      </section>

      <DevisCta />
    </div>
  );
}
